import cx from 'clsx';
import PropTypes from 'prop-types';
import { createContext, useContext } from 'react';

import Container from 'components/Container';

const ThemeContext = createContext('white');

const Section = ({ paddings = 'default', theme = 'white', ...props }) => (
  <section
    className={cx(
      {
        'pt-12 pb-16 md:pb-32': paddings === 'first',
        'py-16 md:py-32': paddings === 'default',
        'bg-linear-to-b from-gray-50 to-gray-100': theme === 'gradient-light',
        'bg-linear-to-b from-accent-500 to-accent-600 text-white': theme === 'gradient-accent',
        'bg-gray-100': theme === 'light',
        'bg-gray-900': theme === 'dark',
        'bg-accent-500': theme === 'accent',
      },
      props.className,
    )}
  >
    <Container className="space-y-12" id={props.id}>
      <ThemeContext.Provider value={theme}>{props.children}</ThemeContext.Provider>
    </Container>
  </section>
);

Section.propTypes = {
  paddings: PropTypes.oneOf(['default', 'first', 'manual']),
  theme: PropTypes.oneOf(['white', 'light', 'dark', 'accent', 'gradient-light', 'gradient-accent']),
  id: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

const Header = ({ level = 2, size = 'normal', center = false, ...props }) => {
  const HeadingComponent = `h${level}`;
  const theme = useContext(ThemeContext);
  return (
    <header
      className={cx(
        {
          'mx-auto text-center': center,
          'max-w-2xl': size !== 'large',
          'max-w-3xl': size === 'large',
        },
        props.className,
      )}
    >
      <HeadingComponent
        className={cx(
          'inline-flex font-bold',
          {
            'text-3xl sm:text-4xl md:leading-tight tracking-tight': size === 'normal',
            'text-4xl md:text-6xl md:leading-tight tracking-wide': size === 'medium',
            'text-5xl sm:text-7xl md:leading-tight': size === 'large',
          },
          {
            'text-gray-900':
              ['white', 'light'].includes(theme) ||
              (theme === 'gradient-light' && size === 'normal'),
            'text-white': ['gradient-accent', 'dark'].includes(theme),
            'bg-linear-to-r from-gray-800 via-gray-600 to-gray-800 bg-clip-text text-transparent':
              theme === 'gradient-light' && size !== 'normal',
          },
        )}
      >
        {props.title}
      </HeadingComponent>
      {/* Top margin on the content, not `space-y-*` on the header: the heading is inline-flex,
          and Tailwind 4 would put the margin under it, inside its line box. Rendered even when
          empty, as before (pages rely on its margin). */}
      <div
        className={cx({
          'mt-4': size === 'normal',
          'mt-6': size === 'medium',
          'mt-8': size === 'large',
        })}
      >
        {props.children}
      </div>
    </header>
  );
};

Header.propTypes = {
  level: PropTypes.oneOf([1, 2, 3]),
  size: PropTypes.oneOf(['normal', 'medium', 'large']),
  center: PropTypes.bool,
  title: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

Section.Header = Header;
Section.ThemeContext = ThemeContext;

export default Section;
